// src/components/HelpSupport/TermsService.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
 
const C = {
    primary:          '#6a37d4',
    primaryLight:     '#ae8dff',
    primaryContainer: '#ede8ff',
    secondary:        '#006947',
    secondaryContainer: '#d1fae5',
    surface:          '#f3f6ff',
    surfaceLow:       '#eaf1ff',
    surfaceHigh:      '#d6e3f9',
    tertiaryContainer: '#fed01b',
    tertiary:         '#6f5900',
    onBackground:     '#252f3c',
    onSurface:        '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant:   '#a4aebe',
};
 
// ── Shared typography ─────────────────────────────────────────────────────────
const H2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.35rem', fontWeight: '900', color: C.onBackground, margin: '40px 0 12px 0', paddingBottom: '8px', borderBottom: `2px solid ${C.surfaceHigh}` }}>
        {children}
    </h2>
);
const H3: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.05rem', fontWeight: '800', color: C.primary, margin: '24px 0 8px 0' }}>
        {children}
    </h3>
);
const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <p style={{ fontSize: '0.95rem', lineHeight: '1.8', color: C.onSurface, margin: '0 0 14px 0', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {children}
    </p>
);
const UL: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <ul style={{ margin: '0 0 16px 0', paddingLeft: '20px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{children}</ul>
);
const OL: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <ol style={{ margin: '0 0 16px 0', paddingLeft: '20px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{children}</ol>
);
const LI: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <li style={{ fontSize: '0.95rem', lineHeight: '1.8', color: C.onSurface, marginBottom: '6px' }}>{children}</li>
);
 
const TermsService: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'kids' | 'parents'>('kids');
 
    return (
        <div style={{ minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif", padding: '48px 24px 80px' }}>
            <div style={{ maxWidth: '760px', margin: '0 auto' }}>
 
                {/* Header */}
                <div style={{ marginBottom: '36px' }}>
                    <div style={{ display: 'inline-block', background: C.primaryContainer, color: C.primary, padding: '4px 14px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', fontFamily: "'Epilogue', sans-serif", marginBottom: '16px' }}>
                        Legal
                    </div>
                    <h1 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '2.4rem', fontWeight: '900', color: C.onBackground, margin: '0 0 8px 0' }}>
                        Terms of Service
                    </h1>
                    <p style={{ fontSize: '0.85rem', color: C.onSurfaceVariant, margin: 0 }}>
                        Last updated: November 10, 2025
                    </p>
                </div>
 
                {/* Tab switcher */}
                <div style={{
                    display: 'flex', gap: '8px', marginBottom: '28px',
                    background: '#fff', padding: '6px', borderRadius: '14px',
                    border: `1px solid ${C.surfaceHigh}`,
                    width: 'fit-content',
                }}>
                    {([
                        { id: 'kids',    label: '👦 For Kids' },
                        { id: 'parents', label: '👨‍👩‍👧 For Parents' },
                    ] as const).map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                padding: '10px 24px',
                                borderRadius: '10px', border: 'none',
                                fontSize: '0.9rem', fontWeight: '700',
                                cursor: 'pointer',
                                fontFamily: "'Epilogue', sans-serif",
                                transition: 'all 0.15s',
                                background: activeTab === tab.id
                                    ? `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`
                                    : 'transparent',
                                color: activeTab === tab.id ? '#fff' : C.onSurfaceVariant,
                                boxShadow: activeTab === tab.id ? `0 2px 8px ${C.primary}30` : 'none',
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
 
                {/* ── KIDS TAB ────────────────────────────────────────────────── */}
                {activeTab === 'kids' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
 
                        {/* Welcome */}
                        <div style={{ background: '#fff', borderRadius: '20px', padding: '32px', border: `1px solid ${C.surfaceHigh}`, boxShadow: '0 4px 24px rgba(0,0,0,0.05)' }}>
                            <H2>👋 Welcome to Our Rules</H2>
                            <P>Just like your school or home has rules to keep everyone safe and happy, our platform has rules too! These rules help make sure everyone can have fun writing stories and being creative.</P>
                            <P><strong>Important:</strong> Before you use our platform, your parent or guardian needs to read and agree to the full terms. But we want YOU to understand the basics too!</P>
                        </div>
 
                        {/* What you can/can't do */}
                        <div style={{ background: '#fff', borderRadius: '20px', padding: '32px', border: `1px solid ${C.surfaceHigh}`, boxShadow: '0 4px 24px rgba(0,0,0,0.05)' }}>
                            <H2>✨ The Basic Rules</H2>
                            <H3>What You Can Do</H3>
                            <UL>
                                <LI>✅ Write awesome stories and share them</LI>
                                <LI>✅ Read stories from other young writers</LI>
                                <LI>✅ Leave nice comments and encourage other writers</LI>
                                <LI>✅ Create cool book covers and illustrations</LI>
                                <LI>✅ Earn badges and points for your great work</LI>
                                <LI>✅ Learn new writing skills and improve</LI>
                            </UL>
                            <H3>What You Can't Do</H3>
                            <UL>
                                <LI>❌ Share your personal information (address, phone number, or school)</LI>
                                <LI>❌ Be mean or bully other writers</LI>
                                <LI>❌ Copy someone else's story and say it's yours</LI>
                                <LI>❌ Write stories with bad words or inappropriate content</LI>
                                <LI>❌ Share your password with anyone (except your parents)</LI>
                                <LI>❌ Try to break or hack the website</LI>
                            </UL>
                        </div>
 
                        {/* Your stories */}
                        <div style={{ background: '#fff', borderRadius: '20px', padding: '32px', border: `1px solid ${C.surfaceHigh}`, boxShadow: '0 4px 24px rgba(0,0,0,0.05)' }}>
                            <H2>📚 Your Stories Belong to YOU!</H2>
                            <P><strong>Good news:</strong> Every story you write belongs to YOU! You're the author and creator.</P>
                            <P>When you share a story on our platform, you're giving us permission to:</P>
                            <UL>
                                <LI>Show your story to other users on the platform</LI>
                                <LI>Display it in the "Featured Stories" section (if it's really good!)</LI>
                                <LI>Keep a copy so you never lose your work</LI>
                            </UL>
                            <P><strong>But remember:</strong> Your story is still yours! You can delete it anytime, and we won't use it for anything else.</P>
                        </div>
 
                        {/* Be kind */}
                        <div style={{ background: C.primaryContainer, borderRadius: '20px', padding: '32px', border: `1px solid ${C.primary}20` }}>
                            <H2>💙 Be Kind and Respectful</H2>
                            <P>Everyone here loves writing and wants to have fun! To keep our community awesome, remember:</P>
                            <UL>
                                <LI>🌟 <strong>Be Encouraging:</strong> Say nice things about other people's stories</LI>
                                <LI>🌟 <strong>Be Respectful:</strong> Treat everyone the way you want to be treated</LI>
                                <LI>🌟 <strong>Be Helpful:</strong> If you give feedback, make it kind and helpful</LI>
                                <LI>🌟 <strong>Be Original:</strong> Write your own stories, don't copy others</LI>
                                <LI>🌟 <strong>Be Safe:</strong> Follow all the safety rules</LI>
                            </UL>
                            <div style={{ background: C.primary, color: '#fff', borderRadius: '12px', padding: '14px 18px', fontSize: '0.9rem', fontWeight: '700', fontFamily: "'Epilogue', sans-serif", marginTop: '8px' }}>
                                If someone breaks these rules, use the Report button 🚩 and tell an adult!
                            </div>
                        </div>
 
                        {/* Rule breaking */}
                        <div style={{ background: '#fff', borderRadius: '20px', padding: '32px', border: `1px solid ${C.surfaceHigh}`, boxShadow: '0 4px 24px rgba(0,0,0,0.05)' }}>
                            <H2>⚠️ What If Someone Breaks the Rules?</H2>
                            <P>We want everyone to have a good experience! If someone doesn't follow the rules:</P>
                            <OL>
                                <LI><strong>First Warning:</strong> We'll send them a reminder about the rules</LI>
                                <LI><strong>Second Warning:</strong> They might not be able to use some features for a little while</LI>
                                <LI><strong>Serious Problems:</strong> If someone keeps breaking rules or does something really bad, we might have to remove their account</LI>
                            </OL>
                            <P>We don't want to do this! We'd rather help everyone learn and follow the rules. 😊</P>
                        </div>
 
                        {/* Summary cards */}
                        <div style={{ background: '#fff', borderRadius: '20px', padding: '32px', border: `1px solid ${C.surfaceHigh}`, boxShadow: '0 4px 24px rgba(0,0,0,0.05)' }}>
                            <H2>🎯 Quick Summary</H2>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginTop: '16px' }}>
                                {[
                                    { icon: '✍️', title: 'Write & Create', desc: 'Make amazing stories and have fun!', bg: C.primaryContainer },
                                    { icon: '🛡️', title: 'Stay Safe',      desc: 'Never share personal information',   bg: C.secondaryContainer },
                                    { icon: '💖', title: 'Be Kind',        desc: 'Treat others with respect',          bg: `${C.tertiaryContainer}80` },
                                    { icon: '🎨', title: 'Be Original',    desc: 'Create your own unique stories',     bg: C.surfaceLow },
                                ].map(card => (
                                    <div key={card.title} style={{ background: card.bg, borderRadius: '14px', padding: '20px', textAlign: 'center' }}>
                                        <div style={{ fontSize: '2rem', marginBottom: '8px' }}>{card.icon}</div>
                                        <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.95rem', color: C.onBackground, marginBottom: '4px' }}>{card.title}</div>
                                        <div style={{ fontSize: '0.8rem', color: C.onSurfaceVariant }}>{card.desc}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
 
                {/* ── PARENTS TAB ─────────────────────────────────────────────── */}
                {activeTab === 'parents' && (
                    <div style={{ background: '#fff', borderRadius: '20px', padding: '48px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: `1px solid ${C.surfaceHigh}` }}>
 
                        <P>These Terms of Service ("Terms") govern your access to and use of Scriblish's platform, website, and services. By creating an account or using our Service, you agree to be bound by these Terms.</P>
                        <div style={{ background: C.primaryContainer, borderRadius: '12px', padding: '14px 18px', marginBottom: '24px', fontSize: '0.9rem', color: C.primary, fontWeight: '600', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                            <strong>IMPORTANT:</strong> If you are creating an account for a child under 13, you must be the child's parent or legal guardian, and you agree to these Terms on behalf of your child.
                        </div>
 
                        <H2>1. Acceptance of Terms</H2>
                        <P>By accessing or using Scriblish, you agree to:</P>
                        <UL>
                            <LI>Be bound by these Terms of Service</LI>
                            <LI>Comply with our Privacy Policy and Community Guidelines</LI>
                            <LI>Comply with all applicable local, state, national, and international laws</LI>
                        </UL>
 
                        <H2>2. Eligibility and Account Requirements</H2>
                        <H3>2.1 Age Requirements</H3>
                        <P>The Service is intended for children ages 8–12 under parental supervision.</P>
                        <UL>
                            <LI><strong>Children under 13:</strong> Must have verifiable parental consent (COPPA)</LI>
                            <LI><strong>Children 13–14:</strong> May create accounts with parental knowledge and consent</LI>
                            <LI><strong>Parents/Guardians:</strong> Must be 18+ to create and manage child accounts</LI>
                        </UL>
 
                        <H3>2.2 Account Responsibilities</H3>
                        <UL>
                            <LI>Maintaining the confidentiality of your account credentials</LI>
                            <LI>All activities that occur under your account</LI>
                            <LI>Notifying us immediately of any unauthorized use</LI>
                            <LI>Ensuring all information provided is accurate and current</LI>
                        </UL>
 
                        <H3>2.3 Parental Controls</H3>
                        <UL>
                            <LI>Review all content created or accessed by their child</LI>
                            <LI>Revoke consent and request deletion of their child's account</LI>
                            <LI>Restrict their child's ability to share content publicly</LI>
                            <LI>Access and manage all account settings and privacy controls</LI>
                        </UL>
 
                        <H2>3. User Content and Intellectual Property</H2>
                        <H3>3.1 Ownership of User Content</H3>
                        <P>Users retain all ownership rights to the content they create. By submitting content, you grant Scriblish a non-exclusive, worldwide, royalty-free license to:</P>
                        <UL>
                            <LI>Host, store, and display your content on the platform</LI>
                            <LI>Make your content available to other users per your sharing settings</LI>
                            <LI>Create backups necessary to provide the Service</LI>
                            <LI>Feature outstanding work in promotional materials (with explicit consent)</LI>
                        </UL>
 
                        <H3>3.2 Content Standards</H3>
                        <UL>
                            <LI>Be appropriate for children ages 8–12</LI>
                            <LI>Not contain personal identifying information</LI>
                            <LI>Not infringe on others' intellectual property rights</LI>
                            <LI>Comply with our Community Guidelines</LI>
                            <LI>Not contain profanity, violence, sexual content, or hate speech</LI>
                        </UL>
 
                        <H3>3.3 Content Moderation</H3>
                        <P>Scriblish reserves the right to review, monitor, and remove content that violates these Terms, use automated systems to detect inappropriate content, and disable accounts that repeatedly violate content standards.</P>
 
                        <H2>4. Prohibited Conduct</H2>
                        <UL>
                            <LI>Impersonate others or provide false information</LI>
                            <LI>Harass, bully, or threaten other users</LI>
                            <LI>Share personal information (addresses, phone numbers, school names)</LI>
                            <LI>Attempt to circumvent safety or security features</LI>
                            <LI>Use automated systems, bots, or scrapers</LI>
                            <LI>Upload malicious code, viruses, or malware</LI>
                            <LI>Use the Service for any illegal purpose</LI>
                        </UL>
 
                        <H2>5. Privacy and Data Protection</H2>
                        <H3>5.1 COPPA Compliance</H3>
                        <UL>
                            <LI>Obtain verifiable parental consent before collecting information from children under 13</LI>
                            <LI>Collect only the minimum information necessary to provide the Service</LI>
                            <LI>Allow parents to review, update, or delete their child's information</LI>
                            <LI>Never sell or rent children's personal information to third parties</LI>
                        </UL>
 
                        <H3>5.2 Data Security</H3>
                        <P>We implement industry-standard security measures including encryption, secure servers, and regular security audits to protect user data.</P>
 
                        <H2>6. Subscription and Payment Terms</H2>
                        <H3>6.1 Free Service</H3>
                        <P>Basic access to Scriblish is free with limited features.</P>
 
                        <H3>6.2 Billing and Cancellation</H3>
                        <UL>
                            <LI>Subscriptions renew automatically until cancelled</LI>
                            <LI>You may cancel at any time from your account settings</LI>
                            <LI>Cancellations take effect at the end of the current billing period</LI>
                            <LI>No refunds for partial months</LI>
                            <LI>We reserve the right to change pricing with 30 days notice</LI>
                        </UL>
 
                        <H2>7. Disclaimers and Limitations of Liability</H2>
                        <H3>7.1 Service "As Is"</H3>
                        <P>The Service is provided "as is" without warranties of any kind. We do not guarantee the Service will be uninterrupted, secure, or error-free.</P>
 
                        <H3>7.2 Limitation of Liability</H3>
                        <P>To the maximum extent permitted by law, Scriblish shall not be liable for indirect, incidental, or consequential damages, loss of data or profits, service interruptions, or third-party conduct. Our total liability shall not exceed the amount paid by you in the 12 months preceding the claim.</P>
 
                        <H2>8. Termination</H2>
                        <H3>8.1 By User</H3>
                        <UL>
                            <LI>Your access to the Service will cease immediately upon termination</LI>
                            <LI>You may download your content before deletion</LI>
                            <LI>Personal data will be deleted within 30 days per our Privacy Policy</LI>
                        </UL>
 
                        <H3>8.2 By Scriblish</H3>
                        <P>We may suspend or terminate accounts that violate these Terms, engage in illegal activity, pose safety risks, or remain inactive for extended periods.</P>
 
                        <H2>9. Changes to These Terms</H2>
                        <UL>
                            <LI>We will update the "Last Updated" date when changes are made</LI>
                            <LI>For material changes, we will notify users via email or platform notification</LI>
                            <LI>Continued use of the Service after changes constitutes acceptance</LI>
                        </UL>
 
                        <H2>10. Dispute Resolution and Governing Law</H2>
                        <H3>10.1 Governing Law</H3>
                        <P>These Terms are governed by applicable law without regard to conflict of law principles.</P>
 
                        <H3>10.2 Dispute Resolution</H3>
                        <P>Before filing a claim, you agree to contact us at <a href="mailto:scriblishofficial@gmail.com" style={{ color: C.primary, fontWeight: '700', textDecoration: 'none' }}>scriblishofficial@gmail.com</a> to attempt informal resolution.</P>
 
                        <H2>11. Miscellaneous</H2>
                        <H3>11.1 Entire Agreement</H3>
                        <P>These Terms, along with our Privacy Policy and Community Guidelines, constitute the entire agreement between you and Scriblish.</P>
                        <H3>11.2 Severability</H3>
                        <P>If any provision is found unenforceable, the remaining provisions will remain in effect.</P>
                        <H3>11.3 Third-Party Services</H3>
                        <P>The Service may contain links to third-party websites or services. We are not responsible for their content or practices.</P>
 
                        <H2>12. Contact Information</H2>
                        <P>For questions about these Terms, please contact us at <a href="mailto:scriblishofficial@gmail.com" style={{ color: C.primary, fontWeight: '700', textDecoration: 'none' }}>scriblishofficial@gmail.com</a></P>
 
                        {/* Footer links */}
                        <div style={{ marginTop: '48px', paddingTop: '28px', borderTop: `1px solid ${C.surfaceHigh}`, display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
                            {[
                                { label: 'Privacy Policy',       href: '/privacy-policy' },
                                { label: 'Community Guidelines', href: '/community-guidelines' },
                                { label: 'Help & Support',       href: '/help' },
                                { label: 'Contact Us',           href: '/contact' },
                            ].map(({ label, href }) => (
                                <a key={label} href={href} style={{ color: C.primary, textDecoration: 'none', fontSize: '0.85rem', fontWeight: '600', fontFamily: "'Epilogue', sans-serif" }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                                >
                                    {label}
                                </a>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
 
export default TermsService;
